import { createContext, useContext, useEffect, useMemo, useState, useSyncExternalStore, type ComponentType, type ReactNode } from 'react';
import type { RecordingStatus } from '@/ui';

/** Идущая запись приёма в этой вкладке (скрайб в браузере, СКР-09). */
export interface ActiveRecording {
  status: RecordingStatus;
  elapsedSeconds: number;
  stop: () => void;
  pause?: () => void;
  resume?: () => void;
  /** Предупреждения записи поверх любого экрана оболочки (вопрос «Приём ещё идёт?», тишина) — компонент раздела записи. */
  overlay?: ComponentType | null;
  /** Строка для экрана блокировки вместо «запись продолжается» (например, обратный отсчёт до автостопа). */
  notice?: string | null;
}

interface ActiveRecordingContextValue {
  recording: ActiveRecording | null;
  setRecording: (recording: ActiveRecording | null) => void;
}

const ActiveRecordingContext = createContext<ActiveRecordingContextValue | null>(null);

/*
 * Запись, опубликованная вне дерева экрана: движок записи скрайба живёт дольше экрана (врач может открыть
 * расписание во время приёма), поэтому публикует своё состояние сюда, а оболочка показывает его везде.
 */
let externalRecording: ActiveRecording | null = null;
const externalListeners = new Set<() => void>();

/** Опубликовать идущую запись вне React (null — записи нет). */
export function publishActiveRecording(recording: ActiveRecording | null): void {
  externalRecording = recording;
  for (const listener of externalListeners) listener();
}

function subscribeExternal(listener: () => void): () => void {
  externalListeners.add(listener);
  return () => externalListeners.delete(listener);
}

function getExternal(): ActiveRecording | null {
  return externalRecording;
}

/**
 * Статус записи виден всегда (ТЗ 14.1, принцип 3): экран записи регистрирует запись здесь,
 * оболочка показывает её в верхней панели и поверх экрана блокировки (ПОРТ-22).
 */
export function ActiveRecordingProvider({ children }: { children: ReactNode }) {
  const [recording, setRecording] = useState<ActiveRecording | null>(null);
  const external = useSyncExternalStore(subscribeExternal, getExternal, getExternal);
  const value = useMemo(() => ({ recording: recording ?? external, setRecording }), [recording, external]);
  return <ActiveRecordingContext.Provider value={value}>{children}</ActiveRecordingContext.Provider>;
}

export function useActiveRecording(): ActiveRecording | null {
  return useContext(ActiveRecordingContext)?.recording ?? null;
}

/** Экран записи сообщает оболочке о своей записи; при размонтировании запись снимается. */
export function useRegisterRecording(recording: ActiveRecording | null): void {
  const context = useContext(ActiveRecordingContext);
  const setRecording = context?.setRecording;
  useEffect(() => {
    setRecording?.(recording);
  }, [recording, setRecording]);
  useEffect(() => () => setRecording?.(null), [setRecording]);
}
